import i18n from 'i18n';
import { format, startOfToday, differenceInSeconds, isWithinInterval, differenceInMilliseconds } from 'date-fns';
import { humanizer } from 'humanize-duration';
import { pl, uk } from 'date-fns/locale';

// import types
import type { EpgProgram } from 'types/api/epg';

const humanizeDuration = humanizer({
	units: ['h', 'm'],
	delimiter: ' ',
	maxDecimalPoints: 0,
	languages: {
		long: {
			h: () => i18n.t('vod_movieDetails_hours'),
			m: () => i18n.t('vod_movieDetails_minutes')
		},
		short: {
			h: () => 'h',
			m: () => 'm'
		}
	}
});

export const DATE_FORMAT = {
	HOURS_MIN: 'HH:mm',
	DAY_MONTH_YEAR: 'dd.MM.yyyy',
	DATE_WITH_TIME: 'dd.MM.yyyy HH:mm',
	YEAR_MONTH_DAY: 'yyyy-MM-dd',
	DAY_NAME_WITH_DATE: 'EEEE, dd MMMM yyyy',
	DATE_WITH_SECONDS: 'yyyy-MM-dd HH:mm:ss'
};

export const normalizeDate = (date: string | Date) => (typeof date === 'string' ? new Date(date.replace(/-/g, '/')) : new Date(date));

const formatLocale = (date: string | Date, formatStr: string) => {
	if (!date) return '';
	const locale = i18n.language === 'uk' ? uk : pl;
	return format(normalizeDate(date), formatStr, { locale });
};

export const getFormattedDate = (formatStr: string) => (date?: string | Date) => formatLocale(date ?? startOfToday(), formatStr);

export const convertDuration = (seconds: number, language: 'short' | 'long' = 'short') => {
	if (isNaN(seconds)) return '';
	return humanizeDuration(seconds * 1000, { language });
};

export const convertDateToTime = (date: string | Date) => (date ? formatLocale(date, DATE_FORMAT.HOURS_MIN) : '');

export const convertDatesToBroadcastHours = (...dates: string[]) => dates.map(convertDateToTime).join(' - ');

export const getLiveDuration = (since: string | Date, till: string | Date) => {
	if (!since || !till) return 0;
	return differenceInSeconds(normalizeDate(till), normalizeDate(since));
};

export const convertDateToLocale = (date: string) =>
	normalizeDate(date).toLocaleDateString(i18n.language, {
		year: 'numeric',
		month: 'long',
		day: 'numeric'
	});

export const convertDateToYearMonthDay = (date: string) => (date ? formatLocale(date, DATE_FORMAT.YEAR_MONTH_DAY) : '-');

export const getTimeLineFormat = (currentTime: number) => {
	const formatTime = (time: number) => (time >= 10 ? time : `0${time}`);
	const hours = Math.floor(currentTime / 60 / 60);
	const minutes = Math.floor(currentTime / 60) - hours * 60;
	const seconds = Math.floor(currentTime % 60);
	return `${formatTime(hours)}:${formatTime(minutes)}:${formatTime(seconds)}`;
};

export const convertDaysToMonths = (days: number, language: string) => {
	if (!days) return '';
	return humanizeDuration(days * 24 * 60 * 60 * 1000, { units: ['mo'], round: true, language });
};

export const getDurationProgress = (since: string, till: string) => {
	const currentTime = new Date();

	const totalDurationSeconds = getLiveDuration(since, till);
	const elapsedDurationSeconds = differenceInSeconds(currentTime, normalizeDate(since));

	return Math.min((elapsedDurationSeconds / totalDurationSeconds) * 100, 100);
};

export const formatStartDate = (since: string) => {
	const delimiter = i18n.t('sections:program_slide_at_time');
	return formatLocale(since, `d MMMM '${delimiter}' HH:mm`);
};

export const isLiveProgram = (program: EpgProgram) => {
	try {
		const start = normalizeDate(program.since);
		const end = normalizeDate(program.till);
		return isWithinInterval(new Date(), { start, end });
	} catch (err) {
		return false;
	}
};

export const getMillisecondsToEndOfProgram = (till: string) => differenceInMilliseconds(normalizeDate(till), new Date());

export const convertDateToDayMonthYear = (date: string | Date) => formatLocale(date, DATE_FORMAT.DAY_MONTH_YEAR);

export const convertDateToDateWithTime = (date: string | Date) => formatLocale(date, DATE_FORMAT.DATE_WITH_TIME);

export const getCurrentTime = () => convertDateToTime(new Date());

export const convertDateToDayNameWithDate = (date: string | Date) => formatLocale(date, DATE_FORMAT.DAY_NAME_WITH_DATE);

export const convertToDateWithSeconds = (date: string | Date) => formatLocale(date, DATE_FORMAT.DATE_WITH_SECONDS);
